import { useEffect, useMemo, useState } from "react";
import api, { API_BASE, formatApiError, formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, FileSpreadsheet, FileText, Printer, Receipt } from "lucide-react";
import { toast } from "sonner";

function firstOfMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function ReporteVendedoras() {
  const [vendedoras, setVendedoras] = useState([]);
  const [selectedVend, setSelectedVend] = useState("");
  const [desde, setDesde] = useState(firstOfMonthISO());
  const [hasta, setHasta] = useState(todayISO());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const [reciboOpen, setReciboOpen] = useState(false);
  const [reciboVend, setReciboVend] = useState(null);
  const [comisiones, setComisiones] = useState({}); // {order_id: amount}
  const [pagadoPor, setPagadoPor] = useState("");
  const [recibiConforme, setRecibiConforme] = useState("");
  const [fechaPago, setFechaPago] = useState(todayISO());

  useEffect(() => {
    api.get("/reports/vendedoras/lista")
      .then(({ data }) => setVendedoras(data || []))
      .catch(() => {});
  }, []);

  const load = async () => {
    setBusy(true);
    try {
      const params = { desde, hasta };
      if (selectedVend) params.vendedor = selectedVend;
      const { data } = await api.get("/reports/vendedoras", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const descargarExcel = () => {
    const params = new URLSearchParams({ desde, hasta });
    if (selectedVend) params.set("vendedor", selectedVend);
    const token = localStorage.getItem("token");
    fetch(`${API_BASE}/reports/vendedoras.xlsx?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then((r) => r.blob()).then((blob) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `reporte_vendedoras_${desde}_${hasta}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
    }).catch(() => toast.error("No se pudo descargar"));
  };

  const abrirRecibo = (vd) => {
    setReciboVend(vd);
    setComisiones({});
    setPagadoPor("");
    setRecibiConforme(vd.nombre || "");
    setFechaPago(todayISO());
    setReciboOpen(true);
  };

  const totalComision = useMemo(
    () => Object.values(comisiones).reduce((s, v) => s + (Number(v) || 0), 0),
    [comisiones]
  );

  const descargarRecibo = async (fmt) => {
    if (!reciboVend) return;
    const path = fmt === "xlsx"
      ? "/reports/vendedoras/recibo.xlsx"
      : "/reports/vendedoras/recibo.pdf";
    try {
      const { data } = await api.post(path, {
        desde, hasta,
        vendedor_email: reciboVend.email,
        comisiones,
        pagado_por: pagadoPor,
        recibi_conforme: recibiConforme,
        fecha_pago: fechaPago,
      }, { responseType: "blob" });
      const ext = fmt === "xlsx" ? "xlsx" : "pdf";
      const mime = fmt === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";
      const blob = new Blob([data], { type: mime });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `recibo_${reciboVend.email}_${desde}_${hasta}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const imprimirRecibo = async () => {
    if (!reciboVend) return;
    try {
      const { data } = await api.post("/reports/vendedoras/recibo.pdf", {
        desde, hasta,
        vendedor_email: reciboVend.email,
        comisiones,
        pagado_por: pagadoPor,
        recibi_conforme: recibiConforme,
        fecha_pago: fechaPago,
      }, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (w) setTimeout(() => { try { w.print(); } catch {} }, 800);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const t = data?.totales || {};

  return (
    <div className="space-y-6" data-testid="reporte-vendedoras-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Ventas</div>
        <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
          <Users className="w-8 h-8 text-[#002FA7]" /> Reporte Vendedoras
        </h1>
        <p className="text-zinc-500 mt-2 text-sm">Ventas por vendedora agrupadas por fecha. Genera recibo con comisión manual.</p>
      </header>

      <Card className="p-4 flex items-end flex-wrap gap-3" data-testid="rv-filters">
        <div>
          <Label className="text-xs uppercase tracking-widest">Vendedora</Label>
          <Select value={selectedVend || "all"} onValueChange={(v) => setSelectedVend(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="rv-vendedora"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {vendedoras.map((v) => (
                <SelectItem key={v.email} value={v.email}>{v.name || v.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Desde</Label>
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-40" data-testid="rv-desde" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Hasta</Label>
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-40" data-testid="rv-hasta" />
        </div>
        <Button onClick={load} disabled={busy} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="rv-load">
          {busy ? "Cargando..." : "Generar reporte"}
        </Button>
        {data && (
          <Button variant="outline" onClick={descargarExcel} className="gap-2" data-testid="rv-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
        )}
      </Card>

      {data && (
        <>
          {/* Totales */}
          <Card className="p-4 border-2 border-emerald-200 bg-emerald-50/40">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total pedidos</div><div className="text-2xl font-black">{t.total_pedidos}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total artículos</div><div className="text-2xl font-black">{t.total_articulos}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total vendido</div><div className="text-2xl font-black text-emerald-700">{formatMoney(t.total_vendido)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total facturado</div><div className="text-2xl font-black text-blue-700">{formatMoney(t.total_facturado)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pend. facturar</div><div className="text-2xl font-black text-amber-700">{formatMoney(t.total_pendiente_facturar)}</div></div>
            </div>
          </Card>

          {(data.vendedores || []).length === 0 && (
            <Card className="p-8 text-center text-zinc-500">Sin ventas en el rango seleccionado.</Card>
          )}

          {(data.vendedores || []).map((vd) => (
            <Card key={vd.email} className="p-4" data-testid={`rv-vend-${vd.email}`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Vendedora</div>
                  <div className="text-xl font-black">{vd.nombre} <span className="text-sm font-normal text-zinc-500">{vd.email}</span></div>
                </div>
                <Button className="gap-2 bg-emerald-700 hover:bg-emerald-800" onClick={() => abrirRecibo(vd)} data-testid={`rv-recibo-${vd.email}`}>
                  <Receipt className="w-4 h-4" /> Generar recibo
                </Button>
              </div>

              {vd.dias.map((d) => (
                <div key={d.fecha} className="mb-4 border rounded overflow-hidden">
                  <div className="bg-[#002FA7] text-white px-3 py-2 flex flex-wrap gap-3 text-xs font-black uppercase tracking-widest">
                    <span>📅 {d.fecha}</span>
                    <span>Pedidos: {d.cantidad_pedidos}</span>
                    <span>Artículos: {d.cantidad_articulos}</span>
                    <span className="ml-auto">Total del día: {formatMoney(d.total_vendido)}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-100">
                        <tr className="text-left">
                          <th className="px-2 py-1">N° Pedido</th>
                          <th className="px-2 py-1">Cliente</th>
                          <th className="px-2 py-1">Resumen productos</th>
                          <th className="px-2 py-1">Zona</th>
                          <th className="px-2 py-1">Factura</th>
                          <th className="px-2 py-1 text-right">Art.</th>
                          <th className="px-2 py-1 text-right">Monto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {d.pedidos.map((r) => (
                          <tr key={r.id} className="hover:bg-zinc-50">
                            <td className="px-2 py-1 font-mono font-bold">{r.numero_pedido}</td>
                            <td className="px-2 py-1">{r.cliente}</td>
                            <td className="px-2 py-1 text-zinc-700">{r.resumen_productos}</td>
                            <td className="px-2 py-1">{r.zona}</td>
                            <td className="px-2 py-1 font-mono">
                              {r.numero_factura ? r.numero_factura : <span className="text-orange-700 font-black">Sin factura</span>}
                            </td>
                            <td className="px-2 py-1 text-right tabular-nums">{r.cantidad_articulos}</td>
                            <td className="px-2 py-1 text-right tabular-nums font-bold">{formatMoney(r.monto)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              <div className="mt-2 p-3 bg-zinc-100 rounded flex flex-wrap gap-4 text-sm">
                <div><b>Total pedidos:</b> {vd.total_pedidos}</div>
                <div><b>Total artículos:</b> {vd.total_articulos}</div>
                <div><b>Total vendido:</b> <span className="text-emerald-700 font-black">{formatMoney(vd.total_vendido)}</span></div>
                <div><b>Total facturado:</b> <span className="text-blue-700 font-black">{formatMoney(vd.total_facturado)}</span></div>
                <div><b>Pend. facturar:</b> <span className="text-amber-700 font-black">{formatMoney(vd.total_pendiente_facturar)}</span></div>
              </div>
            </Card>
          ))}
        </>
      )}

      {/* Dialog Recibo */}
      <Dialog open={reciboOpen} onOpenChange={setReciboOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Recibo – {reciboVend?.nombre}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-zinc-500 mb-2">
            Rango: {desde} → {hasta}. Escribe la comisión manual por pedido. El sistema suma el total automáticamente.
          </div>

          {reciboVend?.dias.map((d) => (
            <div key={d.fecha} className="mb-3 border rounded">
              <div className="bg-zinc-100 px-3 py-1.5 text-xs font-black uppercase tracking-widest">
                📅 {d.fecha} · Pedidos: {d.cantidad_pedidos} · Artículos: {d.cantidad_articulos}
              </div>
              <table className="w-full text-xs">
                <thead className="bg-zinc-50">
                  <tr className="text-left">
                    <th className="px-2 py-1">N°</th>
                    <th className="px-2 py-1">Cliente</th>
                    <th className="px-2 py-1">Resumen productos</th>
                    <th className="px-2 py-1">Zona</th>
                    <th className="px-2 py-1">Factura</th>
                    <th className="px-2 py-1 text-right">Art.</th>
                    <th className="px-2 py-1 text-right w-32">Comisión Q</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {d.pedidos.map((r) => (
                    <tr key={r.id}>
                      <td className="px-2 py-1 font-mono font-bold">{r.numero_pedido}</td>
                      <td className="px-2 py-1">{r.cliente}</td>
                      <td className="px-2 py-1">{r.resumen_productos}</td>
                      <td className="px-2 py-1">{r.zona}</td>
                      <td className="px-2 py-1 font-mono">{r.numero_factura || "Sin factura"}</td>
                      <td className="px-2 py-1 text-right">{r.cantidad_articulos}</td>
                      <td className="px-2 py-1">
                        <Input
                          type="number" step="0.01" min={0}
                          value={comisiones[r.id] ?? ""}
                          onChange={(e) => setComisiones((c) => ({ ...c, [r.id]: e.target.value }))}
                          className="h-8 text-right"
                          data-testid={`rv-com-${r.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest">Pagado por</Label>
              <Input value={pagadoPor} onChange={(e) => setPagadoPor(e.target.value)} placeholder="Nombre del que paga" data-testid="rv-pagado" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Recibí conforme</Label>
              <Input value={recibiConforme} onChange={(e) => setRecibiConforme(e.target.value)} placeholder="Vendedor(a)" data-testid="rv-recibi" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Fecha de pago</Label>
              <Input type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} data-testid="rv-fecha-pago" />
            </div>
          </div>

          <div className="mt-3 p-3 bg-emerald-50 border-2 border-emerald-200 rounded">
            <div className="flex justify-between items-center">
              <div className="text-sm">
                <div><b>Total pedidos:</b> {reciboVend?.total_pedidos || 0}</div>
                <div><b>Total artículos:</b> {reciboVend?.total_articulos || 0}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest font-black">Total comisión a pagar</div>
                <div className="text-3xl font-black text-emerald-800 tabular-nums" data-testid="rv-total-comision">
                  {formatMoney(totalComision)}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => descargarRecibo("xlsx")} className="gap-2" data-testid="rv-recibo-xlsx">
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </Button>
            <Button variant="outline" onClick={() => descargarRecibo("pdf")} className="gap-2" data-testid="rv-recibo-pdf">
              <FileText className="w-4 h-4" /> PDF
            </Button>
            <Button onClick={imprimirRecibo} className="gap-2 bg-[#002FA7] hover:bg-[#00227A]" data-testid="rv-recibo-print">
              <Printer className="w-4 h-4" /> Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
